import { useState, useEffect } from 'react';
import { MessageSquare, Eye, Calendar, User, UserCheck, UserX, Loader2 } from 'lucide-react';
import Card from '@components/common/Card';
import connectionService from '@services/api/connection.service';
import portfolioCommentService from '@services/api/portfolioComment.service';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { getImageUrl } from '@utils/imageUtils';

const Requests = () => {
  const navigate = useNavigate();
  const [connectionRequests, setConnectionRequests] = useState([]);
  const [portfolioComments, setPortfolioComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchConnectionRequests();
    fetchPortfolioComments();
  }, []);

  const fetchConnectionRequests = async () => {
    try {
      setLoading(true);
      const response = await connectionService.getAllConnectionRequests();
      if (response.success) {
        setConnectionRequests(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch connection requests:', error);
      toast.error('Failed to load connection requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchPortfolioComments = async () => {
    try {
      setCommentsLoading(true);
      const response = await portfolioCommentService.getMyReceivedComments();
      if (response.success) {
        setPortfolioComments(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch portfolio comments:', error);
      toast.error('Failed to load portfolio comments');
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleAccept = async (connectionId) => {
    try {
      setProcessingId(connectionId);
      const response = await connectionService.acceptConnectionRequest(connectionId);
      if (response.success) {
        toast.success('Connection request accepted');
        setConnectionRequests(connectionRequests.filter(req => req.id !== connectionId));
      }
    } catch (error) {
      console.error('Failed to accept request:', error);
      toast.error('Failed to accept connection request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (connectionId) => {
    try {
      setProcessingId(connectionId);
      const response = await connectionService.rejectConnectionRequest(connectionId);
      if (response.success) {
        toast.success('Connection request rejected');
        setConnectionRequests(connectionRequests.filter(req => req.id !== connectionId));
      }
    } catch (error) {
      console.error('Failed to reject request:', error);
      toast.error('Failed to reject connection request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleViewProfile = (userId) => {
    navigate(`/portfolio/${userId}`);
  };

  return (
    <div className="space-y-3">
      {/* Connection Requests Section */}
      <Card className="!p-3 sm:!p-4">
        <div className="mb-3">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
            Portfolio Access Requests
          </h3>
          <p className="text-xs sm:text-sm text-gray-600">
            Manage requests from users who want to view your portfolio
          </p>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="w-10 h-10 text-gray-400 animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-600">Loading requests...</p>
          </div>
        ) : connectionRequests.length === 0 ? (
          <div className="text-center py-8">
            <Eye className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              No requests
            </h3>
            <p className="text-sm text-gray-600">
              When someone requests to view your portfolio, they'll appear here
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {connectionRequests.map((request) => (
              <div key={request.id} className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    {request.requester?.profilePicture ? (
                      <img
                        src={getImageUrl(request.requester.profilePicture)}
                        alt={`${request.requester.firstName} ${request.requester.lastName}`}
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-gray-200"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-400 to-orange-400 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ display: request.requester?.profilePicture ? 'none' : 'flex' }}
                    >
                      <span className="text-white text-sm sm:text-base font-semibold">
                        {request.requester?.firstName?.[0]}{request.requester?.lastName?.[0]}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm sm:text-base font-semibold text-gray-900">
                          {request.requester?.firstName} {request.requester?.lastName}
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600">
                          {request.requester?.email}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {request.requester?.careerStage && (
                            <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                              {request.requester.careerStage}
                            </span>
                          )}
                          {request.status === 'pending' && (
                            <span className="inline-block px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">
                              Pending
                            </span>
                          )}
                          {request.status === 'accepted' && (
                            <span className="inline-block px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                              Accepted
                            </span>
                          )}
                          {request.status === 'rejected' && (
                            <span className="inline-block px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">
                              Rejected
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-500 flex-shrink-0">
                        <Calendar className="w-3 h-3" />
                        {new Date(request.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                    </div>
                    {request.message && (
                      <div className="mt-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-xs sm:text-sm text-gray-700 italic">"{request.message}"</p>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {request.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleAccept(request.id)}
                            disabled={processingId === request.id}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {processingId === request.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <UserCheck className="w-4 h-4" />
                            )}
                            Accept
                          </button>
                          <button
                            onClick={() => handleReject(request.id)}
                            disabled={processingId === request.id}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <UserX className="w-4 h-4" />
                            Reject
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleViewProfile(request.requester?.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-xs sm:text-sm font-medium hover:bg-gray-50 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        View Profile
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Portfolio Comments & Questions Section */}
      <Card className="!p-3 sm:!p-4">
        <div className="mb-3">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
            Portfolio Comments & Questions
          </h3>
          <p className="text-xs sm:text-sm text-gray-600">
            People who recently commented or asked a question on your portfolio
          </p>
        </div>

        {commentsLoading ? (
          <div className="text-center py-8">
            <Loader2 className="w-10 h-10 text-gray-400 animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-600">Loading comments...</p>
          </div>
        ) : portfolioComments.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              No new comments or questions
            </h3>
            <p className="text-sm text-gray-600">
              When someone comments on your portfolio, they'll appear here
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {portfolioComments.map((comment) => (
              <div key={comment.id} className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    {comment.commenter?.profilePicture ? (
                      <img
                        src={getImageUrl(comment.commenter.profilePicture)}
                        alt={`${comment.commenter.firstName} ${comment.commenter.lastName}`}
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-gray-200"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ display: comment.commenter?.profilePicture ? 'none' : 'flex' }}
                    >
                      <span className="text-white text-sm sm:text-base font-semibold">
                        {comment.commenter?.firstName?.[0]}{comment.commenter?.lastName?.[0]}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="min-w-0 flex-1">
                        <h4 className="text-base font-semibold text-gray-900">
                          {comment.commenter?.firstName} {comment.commenter?.lastName}
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600">
                          {comment.type === 'question' ? 'Asked a question' : 'Left a comment'}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        {comment.type === 'question' && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            Question
                          </span>
                        )}
                        {!comment.isRead && (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                            New
                          </span>
                        )}
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Calendar className="w-3 h-3" />
                          {new Date(comment.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs sm:text-sm text-gray-700">{comment.content}</p>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => navigate(`/portfolio/${comment.commenter?.id}`)}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-xs sm:text-sm font-medium hover:bg-gray-50 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        View Profile
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default Requests;
